import styled from '@emotion/styled';
import { useOverviewContext } from '@resistapp/client/contexts/use-overview-context/use-overview-context';
import { L2Targets } from '@resistapp/common/assays';
import { friendlyL2Target } from '@resistapp/common/friendly';
import { References, useScienceReference } from '../../../hooks/use-science-reference';
import { oldTheme } from '../../shared/old-styles';
import { MetricTitle } from '../../shared/overview-metric-title';
import { RiskScoreIllustration } from './risk-score-illustration';

export function RiskScoreInfo() {
  const { metricGeneCnt, isOneHealthProject, selectedAntibiotic } = useOverviewContext();

  // Create dynamic panel description based on selectedAntibiotic
  const getPanelDescription = () => {
    const baseDescription = isOneHealthProject
      ? `a standardised set of ${metricGeneCnt} clinically relevant`
      : `a custom set of ${metricGeneCnt} clinically relevant`;

    // If a specific antibiotic is selected, include it in the description
    if (selectedAntibiotic) {
      return `${baseDescription} ${friendlyL2Target(selectedAntibiotic as L2Targets)} resistance genes in ${isOneHealthProject ? "Resistomap's OneHealth Panel" : 'the panel'}`;
    }

    // Default description for all ARGs
    return `${baseDescription} ARGs in ${isOneHealthProject ? "Resistomap's OneHealth Panel" : 'the panel'}`;
  };

  const [Ref1, Ref1Num] = useScienceReference({
    number: '1',
    href: 'https://doi.org/10.1016/j.jhazmat.2021.127621',
    text: 'SG Goh et al., 2022, A new modelling framework for assessing the relative…',
  });

  const [Ref2, Ref2Num] = useScienceReference({
    number: '2',
    href: 'https://doi.org/10.1038/s41467-021-25096-3',
    text: 'AN Zhang et al., 2021, An omics-based framework for assessing the health risk…',
  });

  return (
    <div>
      <InfoSection>
        <div>
          <i>
            <MetricTitle noBold noColor />
          </i>{' '}
          is an indicator of overall increase in human population infections that is attributable to
          antibiotic-resistant genes (ARG) spreading through aquatic environments and eventually enabling
          antibiotic-resistant bacteria (ARB) to infect humans through multiple routes.
        </div>
      </InfoSection>
      <InfoSection>
        <b>Scope of genes</b>
        <div style={{ marginTop: 4 }}>The score considers {getPanelDescription()}.</div>
      </InfoSection>
      <InfoSection>
        <b>Metric calculation</b>
        {/* TODO: Add the actual calculation formula, similar to IndexInfo */}
        <div style={{ marginTop: 4 }}>
          The risk score ranges from 0 to 100 and is calcluated from ARG copy numbers per L in a piecewise-linear
          manner. The range from 0 to 10 is normalised to most polluted European surface water samples, and the range
          from 10 to 100 is normalised to the most polluted wastewater samples.
        </div>
        <div style={{ marginTop: 4 }}>
          The score is calculated as a risk-weighted sum of copy numbers of each genes, relative to the sum in most
          polluted samples among thousands of natural and wastewater samples in the Resistomap database.
          <Ref1Num />
          <div style={{ marginTop: 4 }}>
            The clinical health risk weights are obtained by classifying the ARGs into four risk categories based on
            three criteria: <i>Enrichment in human-associated environments</i>, <i>gene mobility</i>, and{' '}
            <i>host pathogenicity</i>.
            <Ref2Num />
          </div>
        </div>
      </InfoSection>
      <InfoSection>
        <b>Biological meaning</b>
        {/* TODO: Add a visualization or detailed explanation of risk levels, similar to IndexInfo's LevelInfo */}
        <div style={{ marginTop: 12 }}>
          A risk score of 0 corresponds to a clean sample with no quantifiable ARGs, such as spring water. A risk score
          of 10 indicates risk levels similar the most polluted surface water samples in Europe, while a risk score of
          100 indicates risk similar to the most polluted, raw wastewater observed globally in the Resistomap database.
        </div>
        <div style={{ marginTop: 12 }}>The below graph illustrates the risk score scale:</div>
        <RiskScoreIllustration />
      </InfoSection>
      <InfoSection>
        <References style={{ marginTop: 25 }}>
          <Ref1 />
          <Ref2 />
        </References>
      </InfoSection>
    </div>
  );
}

const InfoSection = styled.div`
  padding-bottom: ${oldTheme.spacing.xs};
`;

// TODO: Add MathIntend if needed for formula display
// export const MathIntend = styled.div`
//   padding-left: ${oldTheme.spacing.m};
//   padding-top: ${oldTheme.spacing.xs};
//   padding-bottom: ${oldTheme.spacing.xs};
// `;
